import { forwardRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { PdfTargetSection } from './components';
import { PDF_DATE_FORMAT, PDF_PAGE_BREAK_STYLE, PDF_PAGE_STYLE } from './constants';
import {
  PDF_META_CLIENT_COLUMN_SX,
  PDF_META_DATES_COLUMN_SX,
  PDF_META_ROW_SX,
  PDF_ROOT_SX,
  PDF_TITLE_SX,
} from './styles';
import { transformTrialSummary } from './utils';

import type { PdfDocumentProps } from './types';

const PdfDocument = forwardRef<HTMLDivElement, PdfDocumentProps>(function PdfDocument(props, ref) {
  const { data, client, treatmentPlan, goals, recommendation } = props;

  const transformedData = useMemo(
    () => (data ? transformTrialSummary(data, goals) : []),
    [data, goals],
  );

  if (!data || !client || !treatmentPlan || !goals || !recommendation) return null;

  const authorizationStart = recommendation.date_start
    ? moment(recommendation.date_start).format(PDF_DATE_FORMAT)
    : 'N/A';
  const authorizationEnd =
    recommendation.date_start && recommendation.duration
      ? moment(recommendation.date_start)
          .add(recommendation.duration, 'months')
          .format(PDF_DATE_FORMAT)
      : 'N/A';

  return (
    <Box ref={ref} sx={PDF_ROOT_SX}>
      <Typography variant="h3" sx={PDF_TITLE_SX}>
        Treatment plan goals report
      </Typography>
      <style>{PDF_PAGE_STYLE}</style>
      <style>{PDF_PAGE_BREAK_STYLE}</style>
      <Box sx={PDF_META_ROW_SX}>
        <Box sx={PDF_META_CLIENT_COLUMN_SX}>
          <Typography sx={{ mb: 1 }}>
            <span style={{ fontWeight: 700 }}>Client name:</span> {client.label}
          </Typography>
          <Typography>
            <span style={{ fontWeight: 700 }}>Treatment plan:</span> {treatmentPlan.label}
          </Typography>
        </Box>
        <Box sx={PDF_META_DATES_COLUMN_SX}>
          <Typography sx={{ mb: 1 }}>
            <span style={{ fontWeight: 700 }}>Authorization start date: </span>
            {authorizationStart}
          </Typography>
          <Typography>
            <span style={{ fontWeight: 700 }}>Authorization end date: </span>
            {authorizationEnd}
          </Typography>
        </Box>
      </Box>
      {transformedData.map((targetData, index) => (
        <PdfTargetSection
          key={`${targetData.goalId}-${targetData.targetId}`}
          targetData={targetData}
          isPageBreak={index % 2 === 1}
        />
      ))}
    </Box>
  );
});

export default PdfDocument;
